import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  Clock, Calendar, TrendingUp, Umbrella, ArrowLeft, 
  AlertTriangle, CheckCircle, ChevronRight, User
} from "lucide-react";
import axios from "axios";
import { API, useLogo, LANGUAGES, Flag } from "../App";
import { toast } from "sonner";

const StaffDashboard = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { logoUrl } = useLogo();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLang, setSelectedLang] = useState("ja");

  useEffect(() => {
    fetchReport();
  }, [employeeId]);

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/attendance/my-report/${employeeId}`);
      setReport(res.data);
      setSelectedLang(res.data.language || "ja");
    } catch (error) {
      console.error("Report fetch error:", error);
      toast.error("レポートの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-slate-900 dark:border-white"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
            <h2 className="text-xl font-bold mb-2">レポートが見つかりません</h2>
            <p className="text-slate-500 mb-6">従業員情報が見つかりませんでした</p>
            <Button onClick={() => navigate("/face-auth")} data-testid="back-to-face-auth">
              認証画面に戻る
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stats = report.stats;
  const employee = report.employee;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 dark:bg-slate-950/80 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate("/face-auth")}
              className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
              data-testid="staff-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">戻る</span>
            </button>
            <div className="flex items-center gap-3">
              <img src={logoUrl} alt="PRC勤怠" className="w-6 h-6 object-contain rounded-lg" />
              <span className="font-bold text-xl">マイレポート</span>
            </div>
            <Select value={selectedLang} onValueChange={setSelectedLang}>
              <SelectTrigger className="w-[140px]" data-testid="report-lang-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map(lang => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="inline-flex items-center gap-2"><Flag countryCode={lang.countryCode} size={16} /> {lang.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Greeting */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" data-testid="greeting-message">
            {report.greeting}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 flex items-center gap-2">
            <Flag countryCode={LANGUAGES.find(l => l.code === selectedLang)?.countryCode || 'jp'} size={16} /> {employee.name} ({employee.employee_code})
          </p>
        </div>

        {/* Alerts */}
        {report.alerts && report.alerts.length > 0 && (
          <div className="mb-8 space-y-3">
            {report.alerts.map((alert, i) => (
              <div 
                key={i}
                className="flex items-center gap-3 p-4 rounded-xl bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/30"
                data-testid={`alert-${i}`}
              >
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-500" />
                <span className="text-yellow-800 dark:text-yellow-200">{alert.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Stats Grid - Bento Layout */}
        <div className="bento-grid mb-8">
          {/* Work Days - Large */}
          <Card className="bento-large bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0" data-testid="stat-work-days">
            <CardContent className="p-8 h-full flex flex-col justify-between">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6" />
                </div>
                <span className="text-white/70 uppercase tracking-wider text-sm">{stats.work_days.label}</span>
              </div>
              <div>
                <div className="text-6xl font-bold tracking-tight mb-2">
                  {stats.work_days.value}
                </div>
                <span className="text-white/50">{stats.work_days.unit}</span>
              </div>
            </CardContent>
          </Card>

          {/* Total Hours */}
          <Card className="stat-card" data-testid="stat-total-hours">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-slate-400" />
                <span className="text-slate-500 text-sm uppercase tracking-wider">{stats.total_hours.label}</span>
              </div>
              <div className="stat-value">{stats.total_hours.value}</div>
              <span className="text-slate-400">{stats.total_hours.unit}</span>
            </CardContent>
          </Card>

          {/* Overtime */}
          <Card className="stat-card" data-testid="stat-overtime">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-red-400" />
                <span className="text-slate-500 text-sm uppercase tracking-wider">{stats.overtime.label}</span>
              </div>
              <div className="stat-value text-red-500">{stats.overtime.value}</div>
              <span className="text-slate-400">{stats.overtime.unit}</span>
            </CardContent>
          </Card>

          {/* Paid Leave - Wide */}
          <Card className="bento-wide bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30" data-testid="stat-paid-leave">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Umbrella className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-emerald-700 dark:text-emerald-300 text-sm uppercase tracking-wider">{stats.paid_leave.label}</span>
                </div>
                <div className="text-4xl font-bold text-emerald-700 dark:text-emerald-400">
                  {stats.paid_leave.value}
                </div>
                <span className="text-emerald-600/70 dark:text-emerald-400/70">{stats.paid_leave.unit}</span>
              </div>
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Umbrella className="w-10 h-10 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Punch History */}
        <Card className="mb-8" data-testid="punch-history-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {report.punch_history.label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>日付</th>
                    <th>出勤</th>
                    <th>退勤</th>
                    <th>労働時間</th>
                    <th>残業</th>
                    <th>状態</th>
                  </tr>
                </thead>
                <tbody>
                  {report.punch_history.records.map((record, i) => (
                    <tr key={record.id || i} data-testid={`punch-record-${i}`}>
                      <td className="font-medium">{record.date}</td>
                      <td>
                        {record.clock_in ? (
                          <span className="text-emerald-600 dark:text-emerald-400">{record.clock_in}</span>
                        ) : (
                          <span className="text-slate-400">--:--</span>
                        )}
                      </td>
                      <td>
                        {record.clock_out ? (
                          <span className="text-red-600 dark:text-red-400">{record.clock_out}</span>
                        ) : (
                          <span className="text-slate-400">--:--</span>
                        )}
                      </td>
                      <td>{record.work_hours || 0}h</td>
                      <td>
                        {record.overtime_hours > 0 && (
                          <Badge variant="destructive">{record.overtime_hours}h</Badge>
                        )}
                      </td>
                      <td>
                        {record.clock_in && record.clock_out ? (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-yellow-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            onClick={() => navigate("/face-auth")}
            className="btn-primary"
            data-testid="back-to-punch-btn"
          >
            打刻画面に戻る
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default StaffDashboard;
